// src/pages/EditCustomerSatisfaction.tsx
import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Customer {
  id?: number;
  customerName: string;
  company: string;
  satisfactionScore: string;
  dealerServiceRating: string;
  comments: string;
}

interface ApiCustomer {
  id: number;
  customer_name: string;
  company: string;
  product_rating: number;
  dealer_service_rating: number;
  comments: string;
}

interface Report { id: number; }

const EditCustomerSatisfaction: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);
  const [newestReportId, setNewestReportId] = useState<number | null>(null);

  // Fetch or create report
  useEffect(() => {
    fetch(`/api/dealers/${id}/reports`)
      .then(r => r.json())
      .then((rs: Report[]) => {
        if (rs.length) setNewestReportId(rs[0].id);
        else
          fetch(`/api/dealers/${id}/reports`, { method: "POST" })
            .then(r => r.json())
            .then(nr => setNewestReportId(nr.id));
      });
  }, [id]);

  // Load existing rows
  useEffect(() => {
    if (!newestReportId) return;
    fetch(`/api/reports/${newestReportId}/customer-satisfaction`)
      .then(r => r.json())
      .then((data: ApiCustomer[]) => {
        if (data.length) {
          setCustomers(
            data.map(d => ({
              id: d.id,
              customerName: d.customer_name,
              company: d.company,
              satisfactionScore: d.product_rating?.toString() || "",
              dealerServiceRating: d.dealer_service_rating?.toString() || "",
              comments: d.comments || ""
            }))
          );
        } else {
          setCustomers([
            {
              customerName: "",
              company: "",
              satisfactionScore: "",
              dealerServiceRating: "",
              comments: ""
            }
          ]);
        }
      });
  }, [newestReportId]);

  const handleChange = (idx: number, field: keyof Customer, val: string) => {
    setCustomers(cs => {
      const copy = [...cs];
      (copy[idx] as any)[field] = val;
      return copy;
    });
  };

  const addRow = () => {
    setCustomers(cs => [
      ...cs,
      { customerName: "", company: "", satisfactionScore: "", dealerServiceRating: "", comments: "" }
    ]);
  };

  // Remove in UI and track ID if existing
  const deleteRow = (idx: number) => {
    setCustomers(cs => {
      const row = cs[idx];
      if (row.id) setDeletedIds(ids => [...ids, row.id!]);
      return cs.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!newestReportId) return;

    // A) DELETE removed rows
    if (deletedIds.length) {
      await Promise.all(
        deletedIds.map(async custId => {
          try {
            const resp = await fetch(
              `/api/reports/${newestReportId}/customer-satisfaction/${custId}`,
              { method: "DELETE" }
            );
            if (!resp.ok && resp.status !== 404) {
              const errBody = await resp.json().catch(() => ({}));
              throw new Error(errBody.error || `Delete failed for id ${custId}`);
            }
          } catch (err) {
            console.error("Error deleting customer id", custId, err);
          }
        })
      );
    }

    // B) Updates vs Creates
    const updates = customers.filter(c => c.id != null);
    const creates = customers.filter(c => c.id == null);

    try {
      for (const c of updates) {
        const res = await fetch(
          `/api/reports/${newestReportId}/customer-satisfaction`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: c.id,
              customer_name: c.customerName,
              company: c.company,
              product_rating: c.satisfactionScore
                ? parseInt(c.satisfactionScore, 10)
                : null,
              dealer_service_rating: c.dealerServiceRating
                ? parseInt(c.dealerServiceRating, 10)
                : null,
              comments: c.comments
            })
          }
        );
        if (!res.ok) throw new Error((await res.json()).error || "Update failed");
      }
      for (const c of creates) {
        const res = await fetch(
          `/api/reports/${newestReportId}/customer-satisfaction`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              dealer_id: parseInt(id!, 10),
              customer_name: c.customerName,
              company: c.company,
              product_rating: c.satisfactionScore
                ? parseInt(c.satisfactionScore, 10)
                : null,
              dealer_service_rating: c.dealerServiceRating
                ? parseInt(c.dealerServiceRating, 10)
                : null,
              comments: c.comments
            })
          }
        );
        if (!res.ok) throw new Error((await res.json()).error || "Create failed");
      }

      navigate(`/dealer/${id}`);
    } catch (err: any) {
      console.error("Save error:", err);
      alert("Error saving customer satisfaction: " + err.message);
    }
  };

  // styling unchanged...
  const container = { backgroundColor: "#F0F5F0", minHeight: "100vh", padding: 20 };
  const card = { backgroundColor: "#FFFFFF", borderRadius: 8, boxShadow: "0 2px 5px rgba(0,0,0,0.1)", maxWidth: 800, margin: "30px auto", padding: 30 };
  const header = { textAlign: "center" as const, color: "#e41e26", borderBottom: "2px solid #e41e26", paddingBottom: 10, marginBottom: 20, fontSize: 24 };
  const label = { display: "block", marginBottom: 5, fontWeight: "bold" };
  const input = { width: "100%", padding: 8, marginBottom: 10, borderRadius: 4, border: "1px solid #ccc" };
  const button = { padding: "5px 10px", backgroundColor: "#e41e26", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", marginRight: 10 };
  const deleteButtonStyle = { ...button, backgroundColor: "transparent", color: "#e41e26", fontSize: 18, padding: "0 8px" };

  return (
    <div style={container}>
      <Navbar />
      <form onSubmit={handleSubmit} style={card}>
        <h1 style={header}>Edit Customer Satisfaction</h1>

        {customers.map((c, i) => (
          <div key={c.id ? `${c.id}-${i}` : `new-${i}`} style={{ marginBottom: 20, border: "1px solid #ccc", padding: 10, borderRadius: 4, position: "relative" }}>
            <button
              type="button"
              onClick={() => deleteRow(i)}
              style={{ ...deleteButtonStyle, position: "absolute", top: 10, right: 10 }}
              title="Delete this row"
            >
              🗑️
            </button>

            <label style={label}>Customer Name:</label>
            <input type="text" value={c.customerName} onChange={e => handleChange(i, "customerName", e.target.value)} style={input} />

            <label style={label}>Company:</label>
            <input type="text" value={c.company} onChange={e => handleChange(i, "company", e.target.value)} style={input} />

            <label style={label}>Product Rating:</label>
            <select value={c.satisfactionScore} onChange={e => handleChange(i, "satisfactionScore", e.target.value)} style={input}>
              <option value="">N/A</option>
              {[1,2,3,4,5].map(n => (
                <option key={n} value={n.toString()}>{n}</option>
              ))}
            </select>

            <label style={label}>Dealer Service Rating:</label>
            <select value={c.dealerServiceRating} onChange={e => handleChange(i, "dealerServiceRating", e.target.value)} style={input}>
              <option value="">N/A</option>
              {[1,2,3,4,5].map(n => (
                <option key={n} value={n.toString()}>{n}</option>
              ))}
            </select>

            <label style={label}>Comments:</label>
            <textarea value={c.comments} onChange={e => handleChange(i, "comments", e.target.value)} style={input} />
          </div>
        ))}

        <div style={{ textAlign: "right" }}>
          <button type="button" onClick={addRow} style={button}>Add Customer</button>
          <button type="submit" style={button}>Save</button>
        </div>
      </form>
    </div>
  );
};

export default EditCustomerSatisfaction;
